import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Fuse from 'fuse.js';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Combobox } from "../components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import EventTriggerManager from "../components/EventTriggerManager";
import { Search, Plus, Filter, X, Upload, Paperclip, Trash2, Loader2, Radar } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import DuplicateChecker from '@/components/DuplicateChecker';
import AuthenticatedMedia from '@/components/AuthenticatedMedia';
import ForesightClassificationTrail from '@/components/ForesightClassificationTrail';
import {
  isForesightClassification,
} from '@/components/foresightClassification';


// Global Department Service - Single Source of Truth with Performance Optimization
const DepartmentService = {
  departments: [],
  listeners: [],
  lastFetch: 0,
  fetchTimeout: null,
  CACHE_DURATION: 30000, // 30 seconds cache
  DEBOUNCE_DELAY: 500, // 500ms debounce
  
  // Add listener for real-time updates
  addListener(callback) {
    this.listeners.push(callback);
  },
  
  // Remove listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  },
  
  // Notify all listeners of department changes
  notify() {
    this.listeners.forEach(callback => callback(this.departments));
  },
  
  // Debounced fetch departments to prevent excessive api calls
  async fetchDepartments() {
    return new Promise((resolve) => {
      // Clear existing timeout
      if (this.fetchTimeout) {
        clearTimeout(this.fetchTimeout);
      }
      
      // Set new debounced timeout
      this.fetchTimeout = setTimeout(async () => {
        const result = await this._actualFetchDepartments();
        resolve(result);
      }, this.DEBOUNCE_DELAY);
    });
  },
  
  // Actual fetch implementation with caching
  async _actualFetchDepartments() {
    try {
      const now = Date.now();
      
      // Return cached data if still valid
      if (this.departments.length > 0 && (now - this.lastFetch) < this.CACHE_DURATION) {
        console.log('🚀 DEPARTMENT SERVICE: Using cached departments (performance optimization)');
        this.notify();
        return this.departments;
      }
      
      console.log('🔗 DEPARTMENT SERVICE: Fetching departments with location counts (optimized single query)');
      
      // PERFORMANCE FIX: Use single optimized endpoint instead of N+1 queries
      const response = await CommanServices.departmentslocationcounts();
      if (response.status) {
        // Filter out null/invalid departments
        const validDepartments = (response.data || []).filter(dept => 
          dept && 
          dept.id && 
          dept.department_name && 
          typeof dept.department_name === 'string'
        );
        
        // CRITICAL FIX: VERSIONED MERGE for departments (mirrors Primary Toggle zones pattern)
        if (this.departments.length === 0) {
          // Initial load - use all fresh data
          this.departments = validDepartments;
        } else {
          // Create maps for efficient lookup
          const prevDepartmentsMap = new Map(this.departments.map(d => [d.id, d]));
          const mergedDepartments = [];
          
          validDepartments.forEach(freshDepartment => {
            const prevDepartment = prevDepartmentsMap.get(freshDepartment.id);
            
            if (!prevDepartment) {
              // New department - use fresh data
              mergedDepartments.push(freshDepartment);
              return;
            }
            
            // VERSIONED MERGE: Only update leader_count if server version is newer AND has valid counts
            const serverVersion = freshDepartment.membersVersion || 0;
            const localVersion = prevDepartment.membersVersion || 0;
            
            // Check if fresh data has DEFINED leader_count field (mirrors Primary Toggle count check)
            const hasValidLeaderCount = typeof freshDepartment.leader_count === 'number';
            
            if (hasValidLeaderCount && serverVersion >= localVersion) {
              // Server data is newer and has valid leader count - use server data
              console.debug(`[departments:merge] id=${freshDepartment.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=applied leader_count=${freshDepartment.leader_count}`);
              mergedDepartments.push(freshDepartment);
            } else if (!hasValidLeaderCount && typeof prevDepartment.leader_count === 'number') {
              // Server data is missing leader count but local has valid count - preserve local
              console.debug(`[departments:merge] id=${freshDepartment.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=skipped(missing_leader_count)`);
              mergedDepartments.push({
                ...freshDepartment,
                leader_count: prevDepartment.leader_count,
                membersVersion: prevDepartment.membersVersion
              });
            } else {
              // Use server data as fallback
              console.debug(`[departments:merge] id=${freshDepartment.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=fallback`);
              mergedDepartments.push(freshDepartment);
            }
          });
          
          this.departments = mergedDepartments;
        }
        this.lastFetch = now;
        
        console.log('✅ DEPARTMENT SERVICE: Fetched', validDepartments.length, 'departments with location counts in single query');
        
        this.notify();
        return this.departments;
      }
      
    } catch (error) {
      console.error("❌ DEPARTMENT SERVICE: Error fetching departments:", error);
      
      // Fallback to basic departments endpoint if optimized endpoint fails
      try {
        console.log('🔄 DEPARTMENT SERVICE: Falling back to basic departments endpoint');
        const fallbackResponse = await CommanServices.departments();
        if (fallbackResponse.status) {
          const basicDepartments = (fallbackResponse.data || []).filter(dept => 
            dept && dept.id && dept.department_name
          );
          
          this.departments = basicDepartments.map(dept => ({
            ...dept,
            authoritative_location_count: (dept.associated_locations || []).length
          }));
          
          this.notify();
          return this.departments;
        }
      } catch (fallbackError) {
        console.error("❌ DEPARTMENT SERVICE: Fallback also failed:", fallbackError);
        this.departments = [];
        this.notify();
        return [];
      }
    }
  },
  
  // Force refresh (bypasses cache)
  async forceRefresh() {
    this.lastFetch = 0;
    return this._actualFetchDepartments();
  },
  
  // Get current departments
  getDepartments() {
    return this.departments;
  }
};

// Make service globally available
window.DepartmentService = DepartmentService;

const ICON_OPTIONS = [
  { value: 'AlertTriangle', label: 'Alert Triangle' },
  { value: 'Flame', label: 'Flame / Fire' },
  { value: 'ShieldAlert', label: 'Shield Alert' },
  { value: 'Skull', label: 'Toxic / Skull' },
  { value: 'Activity', label: 'Pulse / Activity' },
  { value: 'Cloud', label: 'Weather / Cloud' },
  { value: 'Droplets', label: 'Water / Droplets' },
  { value: 'Wind', label: 'Wind / Storm' },
  { value: 'Zap', label: 'Power / Electricity' },
  { value: 'Bell', label: 'Bell / Warning' },
  { value: 'Info', label: 'Info' },
  { value: 'HelpCircle', label: 'Help' },
  { value: 'Users', label: 'Crews / People' },
  { value: 'Bomb', label: 'Bomb / Explosion' },
  { value: 'Crosshair', label: 'Target / Threat' },
  { value: 'Heart', label: 'Safety / Heart' },
  { value: 'Eye', label: 'Observation' },
  { value: 'MapPin', label: 'Location' },
  { value: 'FileText', label: 'Document / Report' },
  { value: 'Wrench', label: 'Maintenance' }
];

const IncidentClassification = () => {
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [selectedTrigger, setSelectedTrigger] = useState("all");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [duplicateValidation, setDuplicateValidation] = useState({
    isChecking: false,
    isDuplicate: false,
    message: '',
    isValid: true
  });

  const isAdmin = useMemo(() => {
    try {
      const user = localStorage.getItem("user");
      const userData = user ? JSON.parse(user) : null;
      return userData?.role === 'admin';
    } catch (e) {
      console.error(e);
      return false;
    }
  }, []);
  
  // Fuse.js configuration for fuzzy search
  const fuseOptions = {
    keys: [
      'incident_type',
      'description',
      'event',
      'alert_trigger_value',
      'alert_level'
    ],
    threshold: 0.3,
    ignoreLocation: true,
    findAllMatches: true
  };
  
  const fuse = useMemo(() => new Fuse(incidents, fuseOptions), [incidents]);
  
  // Handle incident type creation for combobox
  const handleIncidentTypeCreate = async (newType) => {
    if (!newType || !newType.trim()) return;
    
    // Add to suggestions immediately for better UX
    setSuggestions(prev => ({
      ...prev,
      types: [...(prev.types || []), newType].filter((type, index, arr) => 
        arr.findIndex(t => t.toLowerCase() === type.toLowerCase()) === index
      )
    }));
    
    // Update form data
    setFormData({...formData, incident_type: newType});
  };

  // Severity system constants
  const SEVERITY_LEVELS = {
    HIGH: { label: 'High', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-300' },
    MEDIUM: { label: 'Medium', color: 'orange', bgColor: 'bg-orange-100', textColor: 'text-orange-800', borderColor: 'border-orange-300' },
    LOW: { label: 'Low', color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', borderColor: 'border-yellow-300' }
  };

  // Helper function to render severity dot
  const SeverityDot = ({ severity, size = 'w-3 h-3' }) => {
    const colors = {
      HIGH: 'bg-red-500',
      MEDIUM: 'bg-orange-500', 
      LOW: 'bg-yellow-500'
    };
    return <div className={`${size} rounded-full ${colors[severity] || 'bg-gray-300'}`} />;
  };

  // Handle incident-level severity changes with propagation
  const handleIncidentSeverityChange = (newSeverity) => {
    console.log("🔄 INCIDENT SEVERITY: Changing to", newSeverity);
    
    // Update incident severity
    const updatedFormData = {
      ...formData,
      incident_severity: newSeverity,
      alert_level: newSeverity
    };
    
    // Propagate to events that don't have explicit severity
    if (formData.event_triggers && formData.event_triggers.length > 0) {
      updatedFormData.event_triggers = formData.event_triggers.map(eventTrigger => {
        // If event has no explicit severity, inherit from incident
        if (!eventTrigger.severity) {
          return {
            ...eventTrigger,
            // Don't set explicit severity, let it inherit
          };
        }
        return eventTrigger;
      });
    }
    
    setFormData(updatedFormData);
    console.log("✅ INCIDENT SEVERITY: Updated to", newSeverity);
  };

  // Incident Severity Selector Component - Optimized spacing to prevent overflow
  const IncidentSeveritySelector = ({ value, onChange }) => (
    <div className="flex items-center gap-1 w-full">
      {Object.entries(SEVERITY_LEVELS).map(([key, level]) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`flex items-center justify-center gap-1 px-3 h-9 rounded-md text-sm font-medium border shadow-sm transition-all flex-1 min-w-0 ${
            value === key 
              ? `${level.bgColor} ${level.textColor} border-${level.borderColor}` 
              : 'bg-white hover:bg-gray-50 border-input text-gray-700'
          }`}
        >
          <SeverityDot severity={key} size="w-3 h-3" />
          <span className="truncate">{level.label}</span>
        </button>
      ))}
    </div>
  );
  
  const [rightPanelWidth, setRightPanelWidth] = useState(() => Math.floor(window.innerWidth * 0.35)); // Default ~35% of screen width
  const [suggestions, setSuggestions] = useState({
    types: [],
    events: [],
    locations: [],
    departments: []
  });
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploadingMedia(true);
    try {
      const uploadedMedia = [...(formData.media || [])];
      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) {
          alert(`File ${file.name} is too large (max 20MB)`);
          continue;
        }
        const formDataToSend = new FormData();
        formDataToSend.append("files", file);

        const response = await CommanServices.uploadGenericFile(formDataToSend);
        if (response.status) {
          uploadedMedia.push(response.data);
        } else {
          alert(`Failed to upload ${file.name}: ${response.message}`);
        }
      }
      setFormData(prev => ({
        ...prev,
        media: uploadedMedia
      }));
    } catch (error) {
      console.error("Error uploading media:", error);
      alert("Error uploading media file");
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const handleRemoveMedia = (index) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  const [formData, setFormData] = useState({
    incident_type: "",
    incident_severity: "", // No default - user must select
    event_triggers: [],  // New nested structure for Event→Trigger pairing
    // Keep old fields for backward compatibility during migration
    event: [],
    alert_trigger_value: [],
    alert_level: "", // No default - user must select
    description: "",
    media: [],
    icon: "AlertTriangle",
    color: "#4B5563",
    keywords: ""
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentEventInput, setCurrentEventInput] = useState("");
  const [currentTriggerInput, setCurrentTriggerInput] = useState("");
  
  // Extract unique events and triggers from all classifications
  const uniqueEvents = useMemo(() => {
    const eventsSet = new Set();
    incidents.forEach(incident => {
      // From event_triggers
      if (Array.isArray(incident.event_triggers)) {
        incident.event_triggers.forEach(et => {
          if (et.event) eventsSet.add(et.event);
        });
      }
      // From legacy event field
      if (incident.event) {
        if (Array.isArray(incident.event)) {
          incident.event.forEach(e => eventsSet.add(e));
        } else if (typeof incident.event === 'string') {
          eventsSet.add(incident.event);
        }
      }
    });
    return Array.from(eventsSet).sort();
  }, [incidents]);

  const uniqueTriggers = useMemo(() => {
    const triggersSet = new Set();
    incidents.forEach(incident => {
      // From event_triggers
      if (Array.isArray(incident.event_triggers)) {
        incident.event_triggers.forEach(et => {
          if (Array.isArray(et.triggers)) {
            et.triggers.forEach(t => triggersSet.add(t));
          }
        });
      }
      // From legacy alert_trigger_value
      if (incident.alert_trigger_value) {
        if (Array.isArray(incident.alert_trigger_value)) {
          incident.alert_trigger_value.forEach(t => triggersSet.add(t));
        } else if (typeof incident.alert_trigger_value === 'string') {
          triggersSet.add(incident.alert_trigger_value);
        }
      }
    });
    return Array.from(triggersSet).sort();
  }, [incidents]);

  useEffect(() => {
    fetchIncidents();
    fetchSuggestions();
    
    // Subscribe to global department updates
    const handleDepartmentUpdate = () => {
      // Trigger re-render of department selectors
      fetchSuggestions();
    };
    
    DepartmentService.addListener(handleDepartmentUpdate);
    window.addEventListener('departmentUpdated', handleDepartmentUpdate);
    
    return () => {
      DepartmentService.removeListener(handleDepartmentUpdate);
      window.removeEventListener('departmentUpdated', handleDepartmentUpdate);
    };
  }, []);

  // Mouse drag handler for resizable panels
  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = rightPanelWidth;

    const handleMouseMove = (e) => {
      const deltaX = startX - e.clientX;
      const newWidth = Math.max(300, Math.min(800, startWidth + deltaX));
      setRightPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Fuzzy search filtering using Fuse.js and selected filters
  useEffect(() => {
    try {
      let filtered = incidents;

      // 1. Search Term filtering (fuzzy via Fuse.js)
      if (searchTerm.trim()) {
        const results = fuse.search(searchTerm);
        filtered = results.map(result => result.item);
      }

      // 2. Event filtering
      if (selectedEvent && selectedEvent !== "all") {
        filtered = filtered.filter(incident => {
          // Check event_triggers
          if (Array.isArray(incident.event_triggers)) {
            const hasEvent = incident.event_triggers.some(et => et.event === selectedEvent);
            if (hasEvent) return true;
          }
          // Check legacy event
          if (incident.event) {
            if (Array.isArray(incident.event)) {
              return incident.event.includes(selectedEvent);
            } else if (typeof incident.event === 'string') {
              return incident.event === selectedEvent;
            }
          }
          return false;
        });
      }

      // 3. Trigger filtering
      if (selectedTrigger && selectedTrigger !== "all") {
        filtered = filtered.filter(incident => {
          // Check event_triggers
          if (Array.isArray(incident.event_triggers)) {
            const hasTrigger = incident.event_triggers.some(et => 
              Array.isArray(et.triggers) && et.triggers.includes(selectedTrigger)
            );
            if (hasTrigger) return true;
          }
          // Check legacy alert_trigger_value
          if (incident.alert_trigger_value) {
            if (Array.isArray(incident.alert_trigger_value)) {
              return incident.alert_trigger_value.includes(selectedTrigger);
            } else if (typeof incident.alert_trigger_value === 'string') {
              return incident.alert_trigger_value === selectedTrigger;
            }
          }
          return false;
        });
      }

      setFilteredIncidents(filtered);
    } catch (error) {
      console.error('Error filtering incidents:', error);
      setFilteredIncidents(incidents);
    }
  }, [incidents, searchTerm, fuse, selectedEvent, selectedTrigger]);

  // Preserve the catalogue's existing order within each group while keeping
  // ForeSight-detected classifications together at the bottom of the visible
  // result set. This is presentation-only and does not mutate source lineage.
  const displayIncidents = useMemo(() => [
    ...filteredIncidents.filter((incident) => !isForesightClassification(incident)),
    ...filteredIncidents.filter((incident) => isForesightClassification(incident)),
  ], [filteredIncidents]);

  // Filter changes handler removed - using simple search only

  // Multi-select chip functions
  const addEventChip = (value) => {
    if (!value || !value.trim()) return;
    
    const trimmedValue = value.trim();
    const events = Array.isArray(formData.event) ? formData.event : [];
    
    // Case-insensitive deduplication
    const existsAlready = events.some(event => 
      event.toLowerCase() === trimmedValue.toLowerCase()
    );
    
    if (!existsAlready) {
      setFormData({
        ...formData,
        event: [...events, trimmedValue]
      });
    }
    setCurrentEventInput("");
  };

  const removeEventChip = (index) => {
    const events = Array.isArray(formData.event) ? formData.event : [];
    const newEvents = events.filter((_, i) => i !== index);
    setFormData({ ...formData, event: newEvents });
  };

  const addTriggerChip = (value) => {
    if (!value || !value.trim()) return;
    
    const trimmedValue = value.trim();
    const triggers = Array.isArray(formData.alert_trigger_value) ? formData.alert_trigger_value : [];
    
    // Case-insensitive deduplication
    const existsAlready = triggers.some(trigger => 
      trigger.toLowerCase() === trimmedValue.toLowerCase()
    );
    
    if (!existsAlready) {
      setFormData({
        ...formData,
        alert_trigger_value: [...triggers, trimmedValue]
      });
    }
    setCurrentTriggerInput("");
  };

  const removeTriggerChip = (index) => {
    const triggers = Array.isArray(formData.alert_trigger_value) ? formData.alert_trigger_value : [];
    const newTriggers = triggers.filter((_, i) => i !== index);
    setFormData({ ...formData, alert_trigger_value: newTriggers });
  };

  const clearAllFilters = () => {
    setFilters({
      incident_type: "",
      alert_level: "",
      associated_locations: [],
      departments_units: [],
      status: ""
    });
  };

  const fetchIncidents = async () => {
    try {
      // Fetch incident classifications (not reported incidents)
      const response = await CommanServices.incidentClassifications();
      if (response.status) {
        const freshIncidents = [...response.data];
        setIncidents(freshIncidents);
      }
    } catch (error) {
      console.error("Error fetching incident classifications:", error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const [types, events] = await Promise.all([
        CommanServices.classificationssuggestionsType(),
        CommanServices.classificationssuggestionsevents()
      ]);

      if (types.status || events.status) {
        setSuggestions({
          types: types.data,
          events: events.data,
          // Locations now handled by LocationSelector component
          // Departments now handled by DepartmentSelector component
        });
      }
      
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    }
  };

  // Form validation for mandatory fields
  const validateIncidentForm = () => {
    const errors = [];
    
    if (!formData.incident_type?.trim()) {
      errors.push("Incident Type is required");
    }
    
    // Validate event_triggers structure
    // const eventTriggers = Array.isArray(formData.event_triggers) ? formData.event_triggers : [];
    // if (eventTriggers.length === 0) {
    //   errors.push("At least one Event is required");
    // } else {
    //   // Check if at least one event has at least one trigger
    //   const hasValidEventTriggerPair = eventTriggers.some(et => 
    //     et.event?.trim() && Array.isArray(et.triggers) && et.triggers.length > 0
    //   );
      
    //   // For editing existing incidents, allow empty triggers (legacy data compatibility)
    //   // Only require triggers for new incidents
    //   if (!hasValidEventTriggerPair && !isEditing) {
    //     errors.push("Each Event must have at least one Trigger");
    //   }
    // }
    
    return errors;
  };

  const isFormValid = () => {
    const validationErrors = validateIncidentForm();
    const hasValidationErrors = validationErrors.length > 0;
    const hasDuplicateError = duplicateValidation.isDuplicate || duplicateValidation.isChecking;
    
    return !hasValidationErrors && !hasDuplicateError && duplicateValidation.isValid;
  };

  const getValidationMessage = () => {
    const errors = validateIncidentForm();
    if (errors.length === 0) return "";
    
    if (errors.length === 1) {
      return `Please fill in: ${errors[0]}`;
    } else if (errors.length === 2) {
      return `Please fill in: ${errors.join(' and ')}`;
    } else {
      return `Please complete all required fields: ${errors.join(', ')}`;
    }
  };

  const handleSave = async () => {
    try {
      // Validate mandatory fields
      if (!isFormValid()) {
        return; // Save button should be disabled, but extra safety check
      }

      // Set updating state to prevent user interactions during refresh
      setIsUpdating(true);

      // Create save data with array fields properly structured
      const saveData = { 
        ...formData,
        keywords: typeof formData.keywords === 'string'
          ? formData.keywords.split(',').map(s => s.trim()).filter(Boolean)
          : (Array.isArray(formData.keywords) ? formData.keywords : []),
        icon: formData.icon || 'AlertTriangle',
        color: formData.color || '#4B5563'
      };

      let response;
      if (isEditing && selectedIncident) {
        response = await CommanServices.updateincidentclassifications(selectedIncident.id, saveData);
      } else {
        response = await CommanServices.addincidentclassifications(saveData);
      }
      
      if (response && response.status) {
        // Fetch fresh data and wait for it to complete
        await fetchIncidents();
        await fetchSuggestions();
        
        // Small delay, then reset and allow interactions
        setTimeout(() => {
          setSelectedIncident(null);
          setIsEditing(false);
          resetForm();
          setIsUpdating(false); // Re-enable user interactions
        }, 300); // Brief delay to ensure state propagation
        
        // Trigger refresh of user config suggestions if they are being used
        // This ensures cross-system integration updates
        window.dispatchEvent(new CustomEvent('incidentDataUpdated'));
      } else {
        alert(response?.message || "Failed to save incident classification");
        setIsUpdating(false);
      }
    } catch (error) {
      console.error("Error saving incident:", error);
      alert(error.message || "Error saving incident classification");
      // Re-enable interactions even on error
      setIsUpdating(false);
    }
  };


  const handleDelete = async (id) => {
    try {
      await CommanServices.deleteincidentclassifications(id);
      fetchIncidents();
      if (selectedIncident && selectedIncident.id === id) {
        resetForm();
      }
    } catch (error) {
      console.error("Error deleting incident:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      incident_type: "",
      incident_severity: "", // No default - user must select
      event_triggers: [],  // New nested structure
      // Keep old fields for compatibility
      event: [],
      alert_trigger_value: [],
      alert_level: "", // No default - user must select
      // Removed: associated_locations, departments_units, need_admin_confirmation, auto_notify_crew, auto_notify_departments
      description: "",
      media: [],
      icon: "AlertTriangle",
      color: "#4B5563",
      keywords: ""
    });
    setSelectedIncident(null);
    setIsEditing(false);
    setCurrentEventInput("");
    setCurrentTriggerInput("");
  };

  const editIncident = (incident) => {
    // Prevent editing during updates to avoid race conditions
    if (isUpdating) {
      return;
    }
    
    // Handle backward compatibility: if old format exists but new format doesn't, convert it
    let incidentData = { ...incident };
    
    // if ((!incidentData.event_triggers || incidentData.event_triggers.length === 0) && 
    //     incidentData.event && incidentData.alert_trigger_value) {
    //   // Convert old flat structure to new nested structure for editing
    //   incidentData.event_triggers = [{
    //     event: Array.isArray(incidentData.event) && incidentData.event.length > 0 
    //       ? incidentData.event[0] 
    //       : 'Legacy Mixed Events',
    //     triggers: Array.isArray(incidentData.alert_trigger_value) 
    //       ? incidentData.alert_trigger_value 
    //       : [],
    //     severity: null, // No severity data in legacy format
    //     triggerSeverityOverrides: {} // No overrides in legacy format
    //   }];
    // } else
     if (incidentData.event_triggers) {
      // Ensure existing event_triggers have severity fields (backward compatibility)
      incidentData.event_triggers = incidentData.event_triggers.map(et => ({
        ...et,
        severity: et.severity || null,
        triggerSeverityOverrides: et.triggerSeverityOverrides || {}
      }));
    }
    
    // Ensure incident_severity exists (backward compatibility)
    if (!incidentData.incident_severity) {
      incidentData.incident_severity = "MEDIUM"; // Default for legacy incidents
    }
    
    // Ensure event_triggers exists even if empty
    if (!incidentData.event_triggers) {
      incidentData.event_triggers = [];
    }

    // Ensure media exists even if empty
    if (!incidentData.media) {
      incidentData.media = [];
    }
    
    // Set default icon, color, and convert keywords array to comma-separated string
    if (!incidentData.icon) incidentData.icon = "AlertTriangle";
    if (!incidentData.color) incidentData.color = "#4B5563";
    incidentData.keywords = Array.isArray(incident.keywords) 
      ? incident.keywords.join(', ') 
      : (typeof incident.keywords === 'string' ? incident.keywords : '');

    setFormData(incidentData);
    setSelectedIncident(incident);
    setIsEditing(true);
  };

  const duplicateIncident = (incident) => {
    // Handle backward compatibility for duplication
    let incidentData = { ...incident };
    
    if ((!incidentData.event_triggers || incidentData.event_triggers.length === 0) && 
        incidentData.event && incidentData.alert_trigger_value) {
      // Convert old flat structure to new nested structure
      incidentData.event_triggers = [{
        event: Array.isArray(incidentData.event) && incidentData.event.length > 0 
          ? incidentData.event[0] 
          : 'Legacy Mixed Events',
        triggers: Array.isArray(incidentData.alert_trigger_value) 
          ? incidentData.alert_trigger_value 
          : []
      }];
    }

    // Set default icon, color, and convert keywords array to comma-separated string
    if (!incidentData.icon) incidentData.icon = "AlertTriangle";
    if (!incidentData.color) incidentData.color = "#4B5563";
    incidentData.keywords = Array.isArray(incident.keywords) 
      ? incident.keywords.join(', ') 
      : (typeof incident.keywords === 'string' ? incident.keywords : '');
    
    setFormData({
      ...incidentData,
      incident_type: `${incidentData.incident_type} (Copy)`,
      id: undefined
    });
    setSelectedIncident(null);
    setIsEditing(false);
  };

  const getAlertLevelColor = (level) => {
    switch (level) {
      case "High": return "bg-red-100 text-red-800";
      case "Medium": return "bg-orange-100 text-orange-800";
      case "Low": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // Get incident severity colors (using the same system as the SEVERITY_LEVELS)
  const getIncidentSeverityColor = (severity) => {
    switch (severity) {
      case "HIGH": return "bg-red-100 text-red-800";
      case "MEDIUM": return "bg-orange-100 text-orange-800"; 
      case "LOW": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-1 overflow-hidden">

      {/* Main Content */}
      <div className="flex-1 flex relative">
        {/* Center Panel - Incident List */}
        <div className="flex-1 p-6" style={{ marginRight: `${rightPanelWidth + 4}px` }}>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Define all possible incidents</h2>
            <p className="text-sm text-gray-600 mb-4">Click on any incident to view and edit details, or use the buttons below to manage incidents.</p>
            
            {/* Quick Search Bar - consistent with Locations & Zones / Departments & Units */}
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="relative flex-1 max-w-md flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search incidents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Filter Icon Button (Dialog Trigger) */}
                <Dialog open={isFilterModalOpen} onOpenChange={setIsFilterModalOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className={`px-3 relative ${
                        (selectedEvent !== "all" || selectedTrigger !== "all") 
                          ? "border-blue-500 bg-blue-50 text-blue-600 hover:bg-blue-100" 
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Filter className="w-4 h-4" />
                      {(selectedEvent !== "all" || selectedTrigger !== "all") && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-600 rounded-full" />
                      )}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Filter Classifications</DialogTitle>
                      <p className="text-sm text-gray-500">Filter incident classifications specifically by event and trigger.</p>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-600">Event:</label>
                        <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                          <SelectTrigger className="w-full bg-gray-50 border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-md">
                            <SelectValue placeholder="All Events" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Events</SelectItem>
                            {uniqueEvents.map(event => (
                              <SelectItem key={event} value={event}>{event}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-600">Trigger:</label>
                        <Select value={selectedTrigger} onValueChange={setSelectedTrigger}>
                          <SelectTrigger className="w-full bg-gray-50 border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-md">
                            <SelectValue placeholder="All Triggers" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Triggers</SelectItem>
                            {uniqueTriggers.map(trigger => (
                              <SelectItem key={trigger} value={trigger}>{trigger}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <DialogFooter className="flex sm:justify-between gap-2 mt-4">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setSelectedEvent("all");
                          setSelectedTrigger("all");
                        }}
                        disabled={selectedEvent === "all" && selectedTrigger === "all"}
                        className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                      >
                        Reset Filters
                      </Button>
                      <Button onClick={() => setIsFilterModalOpen(false)} className="bg-blue-600 hover:bg-blue-700 text-white">
                        Apply & Close
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Incident
              </Button>
            </div>

            {/* Active Filter Chips */}
            {(selectedEvent !== "all" || selectedTrigger !== "all") && (
              <div className="flex flex-wrap gap-2 items-center mb-4">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Filters:</span>
                
                {selectedEvent !== "all" && (
                  <Badge variant="secondary" className="bg-blue-50 border border-blue-200 text-blue-700 flex items-center gap-1 py-1 px-2.5 rounded-full text-xs">
                    <span>Event: {selectedEvent}</span>
                    <button 
                      onClick={() => setSelectedEvent("all")} 
                      className="ml-1 hover:bg-blue-100 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}

                {selectedTrigger !== "all" && (
                  <Badge variant="secondary" className="bg-blue-50 border border-blue-200 text-blue-700 flex items-center gap-1 py-1 px-2.5 rounded-full text-xs">
                    <span>Trigger: {selectedTrigger}</span>
                    <button 
                      onClick={() => setSelectedTrigger("all")} 
                      className="ml-1 hover:bg-blue-100 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}

                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setSelectedEvent("all");
                    setSelectedTrigger("all");
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 p-1 h-auto"
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-4 max-h-[calc(100vh-250px)] overflow-y-auto pb-6">
            {displayIncidents.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 bg-white border border-gray-200 rounded-lg shadow-sm">
                <Search className="w-12 h-12 text-gray-300 mb-3 animate-pulse" />
                <p className="text-lg font-semibold text-gray-700">No Incidents Found</p>
                <p className="text-sm text-gray-500 text-center max-w-md mt-1">
                  We couldn't find any incident classifications matching your current search term, event, or trigger filters.
                </p>
                {(searchTerm || selectedEvent !== "all" || selectedTrigger !== "all") && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedEvent("all");
                      setSelectedTrigger("all");
                    }}
                    className="mt-4 border-blue-200 text-blue-600 hover:bg-blue-50"
                  >
                    Reset All Filters
                  </Button>
                )}
              </div>
            ) : (
              displayIncidents.map((incident) => (
                <Card 
                  key={incident.id} 
                  className={`cursor-pointer hover:shadow-md transition-all duration-200 border-2 ${
                    selectedIncident?.id === incident.id 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => !isUpdating && editIncident(incident)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div 
                          className="flex items-center justify-center p-2 rounded-lg mt-0.5 border flex-shrink-0"
                          style={{ 
                            backgroundColor: `${incident.color || '#4B5563'}15`,
                            borderColor: `${incident.color || '#4B5563'}30`
                          }}
                        >
                          {(() => {
                            const Icon = LucideIcons[incident.icon || 'AlertTriangle'] || LucideIcons.AlertTriangle;
                            return <Icon className="w-5 h-5" style={{ color: incident.color || '#4B5563' }} />;
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-gray-800 truncate">{incident.incident_type}</h3>
                            {isForesightClassification(incident) && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.07em] text-sky-700"
                                data-testid={`classification-foresight-chip-${incident.id}`}
                                title="Created from a ForeSight detected hazard"
                              >
                                <Radar className="h-2.5 w-2.5" />
                                ForeSight · Detected
                              </span>
                            )}
                          </div>
                          {isForesightClassification(incident) ? (
                            <ForesightClassificationTrail incident={incident} />
                          ) : (
                            <p className="text-sm text-gray-600 font-medium">
                              {Array.isArray(incident.event) ? incident.event.join(', ') : incident.event}
                            </p>
                          )}
                          {incident.keywords && incident.keywords.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {incident.keywords.map((kw, i) => (
                                <Badge key={i} variant="outline" className="px-1.5 py-0 text-[10px] bg-gray-50 border-gray-200 text-gray-600 font-normal">
                                  #{kw}
                                </Badge>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{incident.description}</p>
                          {incident.media && incident.media.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                              <Paperclip className="w-3 h-3" />
                              <span>{incident.media.length} attachment{incident.media.length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={getIncidentSeverityColor(incident.incident_severity)}>
                              {SEVERITY_LEVELS[incident.incident_severity]?.label || incident.incident_severity}
                            </Badge>
                            {(incident.ai_proposed || incident.created_by === 'AI Assistant') && (
                              <span
                                title="This incident type was generated by AI"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700 border border-violet-200 select-none"
                              >
                                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                  <path d="M12 2a1 1 0 0 1 .894.553l2.184 4.423 4.885.71a1 1 0 0 1 .554 1.706l-3.535 3.445.835 4.865a1 1 0 0 1-1.451 1.054L12 16.347l-4.366 2.41a1 1 0 0 1-1.451-1.054l.835-4.865L3.483 9.392a1 1 0 0 1 .554-1.706l4.885-.71L11.106 2.553A1 1 0 0 1 12 2z" />
                                </svg>
                                AI
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Button variant="outline" size="sm" onClick={() => duplicateIncident(incident)}>
                          Copy
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => handleDelete(incident.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Resizable Divider */}
        <div 
          className="w-1 bg-gray-300 hover:bg-blue-400 cursor-col-resize transition-colors flex-shrink-0 relative group"
          onMouseDown={handleMouseDown}
          style={{ position: 'absolute', right: `${rightPanelWidth}px`, top: 0, bottom: 0, zIndex: 10 }}
        >
          <div className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-blue-400 group-hover:opacity-50"></div>
        </div>

        {/* Right Panel - Form */}
        <div 
          className="bg-white shadow-lg p-6 overflow-y-auto max-h-[calc(100vh-81px)] border-l border-gray-200 absolute right-0 top-0 bottom-0"
          style={{ width: `${rightPanelWidth}px` }}
        >
          <h3 className="text-lg font-semibold mb-4">
            {isEditing ? "Edit Incident Classification" : "New Incident Classification"}
          </h3>

          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Incident Type <span className="text-red-500">*</span>
                </label>
                <Combobox
                  value={formData.incident_type}
                  onValueChange={(value) => setFormData({...formData, incident_type: value})}
                  onCreate={handleIncidentTypeCreate}
                  placeholder="Type to search or create incident type..."
                  options={suggestions.types.map(type => ({ value: type, label: type }))}
                  createLabel="Create"
                  className={`${
                    !formData.incident_type?.trim() 
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300'
                  }`}
                />
                <DuplicateChecker
                  module="incident-classifications"
                  value={formData.incident_type}
                  excludeId={selectedIncident?.id}
                  onValidationChange={setDuplicateValidation}
                  disabled={isUpdating}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Incident Severity <span className="text-red-500">*</span>
                </label>
                <IncidentSeveritySelector
                  value={formData.incident_severity}
                  onChange={(severity) => handleIncidentSeverityChange(severity)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Events & Triggers <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500 ml-2">
                  (Each event has its own trigger list for better matching)
                </span>
              </label>
              <EventTriggerManager
                value={formData.event_triggers}
                onChange={(value) => setFormData({...formData, event_triggers: value})}
                incidentSeverity={formData.incident_severity}
                className="mt-2"
              />
            </div>

            {/* Alert Severity Level removed - now handled per-event and per-trigger */}

            {/* Location and Department selectors removed per requirements */}

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                placeholder="Detailed explanation of the incident type..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Keywords</label>
              <Input
                placeholder="e.g. fire, heat, hazard (comma-separated)"
                value={formData.keywords || ''}
                onChange={(e) => setFormData({...formData, keywords: e.target.value})}
              />
            </div>

            {isAdmin && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-t pt-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Custom Icon</label>
                  <Select
                    value={formData.icon || 'AlertTriangle'}
                    onValueChange={(val) => setFormData({ ...formData, icon: val })}
                  >
                    <SelectTrigger className="w-full bg-gray-50 border-gray-300">
                      <SelectValue placeholder="Select Icon" />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map((opt) => {
                        const Icon = LucideIcons[opt.value] || LucideIcons.AlertTriangle;
                        return (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-gray-600" />
                              <span>{opt.label}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Color Theme</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={formData.color || '#4B5563'}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-10 h-10 border border-gray-300 rounded cursor-pointer p-0.5 bg-white flex-shrink-0"
                    />
                    <Input
                      type="text"
                      value={formData.color || '#4B5563'}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      placeholder="#4B5563"
                      className="flex-1 uppercase font-mono"
                      maxLength={7}
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Media / Attachments</label>
              <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 flex flex-col items-center justify-center relative hover:bg-gray-100 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleMediaUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isUploadingMedia}
                />
                <div className="flex flex-col items-center gap-1 text-center pointer-events-none">
                  {isUploadingMedia ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      <span>Uploading file...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-blue-600 font-medium text-sm">Click to upload files</span>
                      <span className="text-[10px] text-gray-500">Supports images, videos, and documents (max 20MB)</span>
                    </>
                  )}
                </div>
              </div>

              {/* Previews */}
              {formData.media && formData.media.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.media.map((item, index) => {
                    const isImage = item.content_type?.startsWith("image/");
                    const isVideo = item.content_type?.startsWith("video/");
                    return (
                      <div key={index} className="relative w-16 h-16 rounded-md border overflow-hidden bg-gray-100 group">
                        {isImage ? (
                          <AuthenticatedMedia as="img" src={item.url} alt={item.filename} className="object-cover w-full h-full" />
                        ) : isVideo ? (
                          <AuthenticatedMedia as="video" src={item.url} className="object-cover w-full h-full" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-1">
                            <Paperclip className="w-4 h-4 text-gray-400 mb-1" />
                            <span className="text-[9px] text-gray-500 font-bold truncate max-w-full" title={item.filename}>{item.filename}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveMedia(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-90 hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Toggles removed per requirements */}

            {/* Validation Message */}
            {(!isFormValid() || duplicateValidation.isDuplicate) && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium">
                  ⚠️ {duplicateValidation.isDuplicate ? duplicateValidation.message : getValidationMessage()}
                </p>
                {!duplicateValidation.isDuplicate && (
                  <p className="text-xs text-red-600 mt-1">
                    Required fields: Incident Type, At least one Event with Triggers
                  </p>
                )}
              </div>
            )}

            {/* Smart Defaults Info removed per requirements */}

            <div className="flex gap-2 pt-4">
              <Button 
                onClick={handleSave} 
                disabled={!isFormValid() || isUpdating}
                className={`flex-1 ${
                  isFormValid() && !isUpdating
                    ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer' 
                    : 'bg-gray-400 cursor-not-allowed opacity-60'
                }`}
              >
                {isUpdating ? 'Updating...' : (isEditing ? "Update" : "Save")}
              </Button>
              <Button variant="outline" onClick={resetForm} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentClassification;
